/**
 * Reusable KB search-as-you-type input with results dropdown.
 *
 * - 220ms debounce, hits GET /api/kb/search
 * - Keyboard nav: ↑ / ↓ to move highlight, Enter to open
 * - Click outside or Esc closes the dropdown
 * - Optional `onSubmit(q)` fires on Enter when no row highlighted
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, FileText, Eye } from "lucide-react";

export default function KbSearchInput({
  value,
  onChange,
  onSubmit,
  placeholder = "Search articles…",
  status = "published",
  testid = "kb-search",
}) {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!value || value.trim().length < 1) {
      setResults([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/kb/search", { params: { q: value.trim(), limit: 8, status } });
        setResults(r.data?.rows || []);
        setActive(-1);
      } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [value, status]);

  // Click-outside
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (row) => {
    setOpen(false);
    navigate(`/knowledge-base/${row.id}`);
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) {
      if (e.key === "Enter" && onSubmit) onSubmit(value);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0) pick(results[active]);
      else if (onSubmit) onSubmit(value);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative flex-1 min-w-[220px]" ref={wrapRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="pl-9 h-9 text-sm"
        data-testid={testid}
      />
      {open && value && (results.length > 0 || !loading) && (
        <div
          className="absolute z-30 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg max-h-[400px] overflow-y-auto"
          data-testid={`${testid}-dropdown`}
        >
          {loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-slate-400" /> No matches.
            </div>
          )}
          {results.map((r, i) => (
            <button
              key={r.id}
              type="button"
              onClick={() => pick(r)}
              onMouseEnter={() => setActive(i)}
              className={`w-full text-left px-3 py-2 border-b border-slate-50 last:border-b-0 hover:bg-indigo-50 ${
                active === i ? "bg-indigo-50" : ""
              }`}
              data-testid={`${testid}-row-${r.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium text-slate-800 truncate">{highlight(r.title, value)}</div>
                <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5 shrink-0">
                  <Eye className="w-3 h-3" />{r.view_count || 0}
                </span>
              </div>
              {r.excerpt && (
                <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{highlight(r.excerpt, value)}</div>
              )}
              <div className="flex items-center gap-1 mt-1 flex-wrap">
                {r.category && (
                  <Badge variant="outline" className="text-[9px] bg-slate-50 border-slate-200 text-slate-600">{r.category}</Badge>
                )}
                {(r.tags || []).slice(0, 3).map((t) => (
                  <Badge key={t} variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[9px]">{t}</Badge>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function highlight(text, q) {
  if (!q || !text) return text;
  const trimmed = q.trim();
  if (!trimmed) return text;
  const re = new RegExp(`(${escapeRe(trimmed)})`, "gi");
  const parts = String(text).split(re);
  return parts.map((p, i) =>
    re.test(p)
      ? <mark key={i} className="bg-amber-200 text-slate-900 rounded-sm px-0.5">{p}</mark>
      : <span key={i}>{p}</span>
  );
}

function escapeRe(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
